import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import Sidebar, { clearSession } from "../components/Sidebar";
import { Dashboard6Styles, PagePreloader, CardHead, PageHeader, KpiStrip } from "../components/ModalUI";
import * as XLSX from "xlsx";
import { applyBillingEnumText, normalizeMaintenanceRequest } from "../utils/enums";
import { isPhaseAdmin } from "../utils/role";

// --- UTILITY FUNCTIONS & CONSTANTS ---
const DEV_PROXY_BASE = "/api";
const PROD_API_BASE = "https://zangon-web-api.azurewebsites.net";
const API_BASE = process.env.NODE_ENV === "development" ? DEV_PROXY_BASE : PROD_API_BASE;
const SESSION_KEY = "zangon_session";

const HOUSE_TYPE_IMAGES = {
  "1 Bedroom Bungalow": "/assets/media/zangon/1bedroombungalo.jpg",
  "1 Bedrooms Bungalow": "/assets/media/zangon/1bedroombungalo.jpg",
  "2 Bedroom Bungalow": "/assets/media/zangon/2bedroom.jpg",
  "2 Bedrooms Bungalow": "/assets/media/zangon/2bedroom.jpg",
  "3 Bedrooms Bungalow": "/assets/media/zangon/3bedroomsbungalo.jpg",
  "3 Bedroom Bungalow": "/assets/media/zangon/3bedroomsbungalo.jpg",
  "4 Bedrooms Duplex": "/assets/media/zangon/4bd.jpg",
  "4 Bedroom Bungalow": "/assets/media/zangon/4bedroombungalo.jpg",
  "4 Bedrooms Bungalow": "/assets/media/zangon/4bedroombungalo.jpg",
  "5 Bedrooms Terrace": "/assets/media/zangon/5-light.jpg",
  "6 Bedroom Bungalow": "/assets/media/zangon/6bedroombungalow.jpg",
  "6 Bedrooms Bungalow": "/assets/media/zangon/6bedroombungalow.jpg",
  "6 Bedroom Terrace Duplex": "/assets/media/zangon/6bedroomterraceduple.jpg",
  "6 Bedrooms Terrace Duplex": "/assets/media/zangon/6bedroomterraceduple.jpg",
};

function getHouseImage(typeName) {
  if (!typeName) return "/assets/media/zangon/3bedroomsbungalo.jpg";
  const norm = typeName.toLowerCase().replace(/[^a-z0-9]/g, "").replace(/bedrooms?/g, "bedroom");
  if (norm.includes("1bedroom") || norm.includes("1bd")) {
    return "/assets/media/zangon/1bedroombungalo.jpg";
  }
  if (norm.includes("2bedroom") || norm.includes("2bd")) {
    return "/assets/media/zangon/2bedroom.jpg";
  }
  if (norm.includes("3bedroombungalow") || norm.includes("3bedroombungalo") || norm.includes("3bd")) {
    return "/assets/media/zangon/3bedroomsbungalo.jpg";
  }
  if (norm.includes("4bedroombungalow") || norm.includes("4bedroombungalo")) {
    return "/assets/media/zangon/4bedroombungalo.jpg";
  }
  if (norm.includes("4bedroomduplex") || norm.includes("4bd")) {
    return "/assets/media/zangon/4bd.jpg";
  }
  if (norm.includes("5bedroom")) {
    const isDark = document.documentElement.getAttribute("data-bs-theme") === "dark" || 
                   localStorage.getItem("kt_theme_mode") === "dark";
    return isDark ? "/assets/media/zangon/5-dark.jpg" : "/assets/media/zangon/5-light.jpg";
  }
  if (norm.includes("6bedroombungalow")) {
    return "/assets/media/zangon/6bedroombungalow.jpg";
  }
  if (norm.includes("6bedroomterraceduplex") || norm.includes("6bedroomterraceduple") || (norm.includes("6bedroom") && norm.includes("terrace"))) {
    return "/assets/media/zangon/6bedroomterraceduple.jpg";
  }
  return HOUSE_TYPE_IMAGES[typeName] || "/assets/media/zangon/3bedroomsbungalo.jpg";
}

function getTenantBuildings(t) {
  if (!t) return [];
  if (Array.isArray(t.currentBuildings) && t.currentBuildings.length > 0) return t.currentBuildings;
  if (Array.isArray(t.assignedBuildings) && t.assignedBuildings.length > 0) return t.assignedBuildings;
  if (Array.isArray(t.buildings) && t.buildings.length > 0) return t.buildings;
  if (t.assignedBuilding) return [t.assignedBuilding];
  if (t.building) return [t.building];
  return [];
}

function buildOccupiedKeysSet(tenantsList) {
  const keys = new Set();
  if (!Array.isArray(tenantsList)) return keys;
  tenantsList.forEach(t => {
    const bList = getTenantBuildings(t);
    bList.forEach(b => {
      if (b.id) keys.add(`id:${b.id}`);
      if (b.buildingId) keys.add(`id:${b.buildingId}`);
      if (b.number && b.phaseName) keys.add(`np:${String(b.phaseName).trim().toLowerCase()}_${String(b.number).trim().toLowerCase()}`);
      else if (b.number) keys.add(`num:${String(b.number).trim().toLowerCase()}`);
    });
  });
  return keys;
}

function isBuildingOccupied(b, occupiedKeys) {
  if (!b) return false;
  if (b.id && occupiedKeys.has(`id:${b.id}`)) return true;
  if (b.buildingId && occupiedKeys.has(`id:${b.buildingId}`)) return true;
  if (b.number && b.phaseName && occupiedKeys.has(`np:${String(b.phaseName).trim().toLowerCase()}_${String(b.number).trim().toLowerCase()}`)) return true;
  if (b.number && occupiedKeys.has(`num:${String(b.number).trim().toLowerCase()}`)) return true;
  return false;
}

function safeJsonParse(text) {
  try { return applyBillingEnumText(JSON.parse(text)); } catch { return null; }
}

function getSession() {
  try { return JSON.parse(localStorage.getItem(SESSION_KEY) || "null"); } catch { return null; }
}

function setSession(session) {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

export function getAccessToken() { return getSession()?.accessToken || ""; }
export function getRefreshToken() { return getSession()?.refreshToken || ""; }

async function fetchWithTimeout(url, options = {}, timeoutMs = 60000) {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } catch (err) {
    if (err?.name === "AbortError") throw new Error("Request timed out. Try again.");
    throw err;
  } finally { clearTimeout(id); }
}

async function refreshAccessToken() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) throw new Error("No refresh token.");
  const res = await fetchWithTimeout(`${API_BASE}/auth/refresh`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken }),
  });
  const text = await res.text();
  const json = text ? safeJsonParse(text) : null;
  if (!res.ok) throw new Error(json?.message || "Token refresh failed.");
  const next = { ...(getSession() || {}), ...(json?.data ?? json ?? {}) };
  setSession(next);
  localStorage.setItem("auth_token", next?.accessToken || "");
  return next;
}

async function authFetch(url, options = {}) {
  let token = getAccessToken();
  const headers = {
    Accept: "application/json",
    Authorization: `Bearer ${token}`,
    ...(options.headers || {}),
  };
  if (options.body && typeof options.body === "string" && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }
  let res = await fetchWithTimeout(url, { ...options, headers });
  if (res.status === 401) {
    try {
      const renewed = await refreshAccessToken();
      token = renewed.accessToken;
      headers.Authorization = `Bearer ${token}`;
      res = await fetchWithTimeout(url, { ...options, headers });
    } catch {
      clearSession();
      window.location.href = "/sign-in";
      throw new Error("Session expired. Please sign in again.");
    }
  }
  return res;
}

function parseArrayData(json) {
  if (!json) return [];
  const raw = json.data;
  if (raw && typeof raw === "object" && Array.isArray(raw.data)) return raw.data;
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    const parsed = safeJsonParse(raw);
    if (Array.isArray(parsed)) return parsed;
    if (parsed && Array.isArray(parsed.data)) return parsed.data;
  }
  if (Array.isArray(json)) return json;
  return [];
}

async function fetchAllTenants(estateId) {
  let allRawTenants = [];
  let page = 0; 
  let hasMore = true;
  while (hasMore) {
    const res = await authFetch(`${API_BASE}/estates/${estateId}/tenants/with-dependents?pageIndex=${page}&pageSize=50`);
    const json = res.ok ? safeJsonParse(await res.text()) : null;
    const items = parseArrayData(json);
    allRawTenants = allRawTenants.concat(items);
    if (items.length < 50) { hasMore = false; } else { page++; }
  }
  return allRawTenants;
}

// Categorical order validated for colour-blind separation; assign in order, never cycle.
const REQ_SERIES = ["#0B9446", "#2563EB", "#D97706", "#9333EA", "#0891B2"];
const REQ_OTHER = "#A1A1AA";

function isClosedRequest(r) {
  const st = (r.status || "").toLowerCase();
  return st.includes("complete") || st.includes("resolve") || st.includes("closed");
}

function RequestsTooltip({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="rp-tooltip">
      <div className="fs-9 fw-semibold text-gray-400 mb-1">Week of {label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="d-flex align-items-center gap-2 fs-8">
          <span className="rp-dot" style={{ background: p.color }} />
          <span className="text-gray-600">{p.name}</span>
          <span className="fw-bold text-gray-900 ms-auto ps-3">{Number(p.value || 0).toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}

function fmtMoney(amount) {
  return new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN", maximumFractionDigits: 0 }).format(amount || 0).replace("NGN", "₦");
}

export default function Reports() {
  const navigate = useNavigate();
  const session = getSession();
  const estateId = session?.estateId || "";

  const [pageReady, setPageReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Filter controls state
  const [timeframe, setTimeframe] = useState("all");
  const [phaseFilter, setPhaseFilter] = useState("all");

  const [reportData, setReportData] = useState({
    totalTenants: 0,
    corporateTenants: 0,
    totalBuildings: 0,
    totalAccessLogs: 0,
    activeVisitors: 0,
    dependentsCount: 0,
    primaryOccupantsCount: 0,
    totalBuildingTypes: 0,
    totalPhases: 0,
    occupiedBuildingsCount: 0,
    
    buildingsList: [],
    buildingTypesList: [],
    tenantsList: [],
    carsList: [],
    requestsList: [],
    
    totalRentCollected: 0,
    pendingRent: 0,
    outstandingPaymentsCount: 0,
    
    totalMaintenanceRequests: 0,
    resolvedRequestsCount: 0,
    pendingRequestsCount: 0,
  });

  useEffect(() => {
    const body = document.getElementById("kt_app_body") || document.body;
    body.id = "kt_app_body";
    body.className = "app-default";
    body.setAttribute("data-kt-app-header-fixed-mobile", "true");
    body.setAttribute("data-kt-app-toolbar-enabled", "true");
    body.setAttribute("data-kt-app-sidebar-enabled", "true");
    body.setAttribute("data-kt-app-sidebar-fixed", "true");

    const init = () => {
      window.KTThemeMode?.init?.();
      window.KTComponents?.init?.();
      window.KTMenu?.createInstances?.();
      window.KTDrawer?.createInstances?.();
      window.KTScroll?.createInstances?.();
      window.KTScrollTop?.createInstances?.();
      window.KTSwapper?.init?.();
    };
    
    init();
    const t = setTimeout(init, 0);
    return () => clearTimeout(t);
  }, []);

  const fetchReportData = useCallback(async () => {
    if (!estateId) {
      setPageReady(true);
      return;
    }
    setIsLoading(true);
    try {
      const [
        btRes, bRes, tRes, poRes, carsRes, requestsRes, totalLogsRes, activeLogsRes, summaryRes
      ] = await Promise.allSettled([
        // Building types are estate-admin only.
        isPhaseAdmin() ? Promise.resolve(null) : authFetch(`${API_BASE}/estates/${estateId}/building-types`),
        authFetch(`${API_BASE}/estates/${estateId}/buildings`),
        fetchAllTenants(estateId),
        authFetch(`${API_BASE}/estates/${estateId}/primary-occupants?pageIndex=0&pageSize=1000`),
        authFetch(`${API_BASE}/estates/${estateId}/cars?pageIndex=0&pageSize=1000`),
        authFetch(`${API_BASE}/estates/${estateId}/maintenance-requests?pageIndex=1&pageSize=1000`),
        authFetch(`${API_BASE}/estates/${estateId}/access-logs?pageIndex=1&pageSize=1`),
        authFetch(`${API_BASE}/estates/${estateId}/access-logs?isActiveVisit=true&pageIndex=1&pageSize=1`),
        authFetch(`${API_BASE}/estates/${estateId}/dashboard-summary`)
      ]);

      let btList = [], bList = [], tList = [], poList = [], carsList = [], requestsList = [];
      let totalLogs = 0, activeLogs = 0, totalRentCollected = 0, pendingRent = 0, outstandingPaymentsCount = 0;

      if (btRes.status === "fulfilled" && btRes.value?.ok) btList = parseArrayData(safeJsonParse(await btRes.value.text()));
      if (bRes.status === "fulfilled" && bRes.value.ok) bList = parseArrayData(safeJsonParse(await bRes.value.text()));
      if (tRes.status === "fulfilled") tList = tRes.value || [];

      if (poRes.status === "fulfilled" && poRes.value.ok) {
        const json = safeJsonParse(await poRes.value.text());
        poList = json?.data?.data || json?.data || [];
      }
      if (carsRes.status === "fulfilled" && carsRes.value.ok) {
        const json = safeJsonParse(await carsRes.value.text());
        carsList = json?.data?.data || json?.data || [];
      }
      if (requestsRes.status === "fulfilled" && requestsRes.value.ok) {
        const json = safeJsonParse(await requestsRes.value.text());
        const rawRequests = json?.data?.data || json?.data || [];
        requestsList = (Array.isArray(rawRequests) ? rawRequests : []).map(normalizeMaintenanceRequest);
      }
      if (totalLogsRes.status === "fulfilled" && totalLogsRes.value.ok) {
        totalLogs = safeJsonParse(await totalLogsRes.value.text())?.data?.totalCount || 0;
      }
      if (activeLogsRes.status === "fulfilled" && activeLogsRes.value.ok) {
        activeLogs = safeJsonParse(await activeLogsRes.value.text())?.data?.totalCount || 0;
      }
      if (summaryRes.status === "fulfilled" && summaryRes.value.ok) {
        const json = safeJsonParse(await summaryRes.value.text());
        if (json?.data) {
          totalRentCollected = json.data.totalRentCollected || 0;
          pendingRent = json.data.pendingRent || 0;
          outstandingPaymentsCount = json.data.outstandingPaymentsCount || 0;
        }
      }

      let corporateCount = 0;
      let totalDeps = 0;
      tList.forEach(t => {
        if (t.isCorporateTenant) corporateCount++;
        if (t.dependentCount || t.dependents?.length) totalDeps += (t.dependentCount || t.dependents?.length || 0);
      });

      const phases = bList.map(b => b.phaseName).filter(Boolean);
      const uniquePhasesCount = [...new Set(phases)].length || 0;

      const occupiedKeys = buildOccupiedKeysSet(tList);
      const occupiedCount = bList.filter(b => isBuildingOccupied(b, occupiedKeys)).length;

      const resolvedRequestsCount = requestsList.filter(r => r.status === "Resolved" || r.status === "Closed" || r.status === "Completed").length;
      const pendingRequestsCount = requestsList.filter(r => r.status !== "Resolved" && r.status !== "Closed" && r.status !== "Completed" && r.status !== "Cancelled").length;

      setReportData({
        totalTenants: tList.length,
        corporateTenants: corporateCount,
        totalBuildings: bList.length,
        totalAccessLogs: totalLogs,
        activeVisitors: activeLogs,
        dependentsCount: totalDeps,
        primaryOccupantsCount: poList.length,
        totalBuildingTypes: btList.length,
        totalPhases: uniquePhasesCount,
        occupiedBuildingsCount: occupiedCount,
        
        buildingsList: bList,
        buildingTypesList: btList,
        tenantsList: tList,
        carsList: carsList,
        requestsList: requestsList,
        
        totalRentCollected,
        pendingRent,
        outstandingPaymentsCount,
        
        totalMaintenanceRequests: requestsList.length,
        resolvedRequestsCount,
        pendingRequestsCount,
      });

    } catch (error) {
      console.error(error);
      toast.error("Failed to compile report data.");
    } finally {
      setIsLoading(false);
      setPageReady(true);
    }
  }, [estateId]);

  const fetchedEstateId = useRef(null);
  useEffect(() => {
    if (fetchedEstateId.current === estateId) return;
    fetchedEstateId.current = estateId;
    fetchReportData();
  }, [fetchReportData, estateId]);

  // Unique phase options
  const uniquePhaseList = useMemo(() => {
    const set = new Set(reportData.buildingsList.map(b => b.phaseName).filter(Boolean));
    return Array.from(set).sort();
  }, [reportData.buildingsList]);

  // Filtered Building list by Phase
  const filteredBuildings = useMemo(() => {
    if (phaseFilter === "all") return reportData.buildingsList;
    return reportData.buildingsList.filter(b => b.phaseName === phaseFilter);
  }, [reportData.buildingsList, phaseFilter]);

  // Filtered Tenants list by Phase
  const filteredTenants = useMemo(() => {
    if (phaseFilter === "all") return reportData.tenantsList;
    return reportData.tenantsList.filter(t => {
      const bList = getTenantBuildings(t);
      return bList.some(b => b.phaseName === phaseFilter || b.phase === phaseFilter);
    });
  }, [reportData.tenantsList, phaseFilter]);

  // Occupancy metrics
  const occupancyRate = useMemo(() => {
    const total = filteredBuildings.length;
    if (total === 0) return 0;
    const occupiedKeys = buildOccupiedKeysSet(filteredTenants);
    const occupiedCount = filteredBuildings.filter(b => isBuildingOccupied(b, occupiedKeys)).length;
    return Math.round((occupiedCount / total) * 100);
  }, [filteredBuildings, filteredTenants]);

  const vacantBuildingsCount = useMemo(() => {
    const total = filteredBuildings.length;
    const occupiedKeys = buildOccupiedKeysSet(filteredTenants);
    const occupiedCount = filteredBuildings.filter(b => isBuildingOccupied(b, occupiedKeys)).length;
    return Math.max(0, total - occupiedCount);
  }, [filteredBuildings, filteredTenants]);

  const unassignedTenantsCount = useMemo(() => {
    return filteredTenants.filter(t => getTenantBuildings(t).length === 0).length;
  }, [filteredTenants]);

  // Chart Computations
  const buildingsPerPhase = useMemo(() => {
    const counts = {};
    reportData.buildingsList.forEach(b => {
      const p = b.phaseName || "General / Phase pending";
      counts[p] = (counts[p] || 0) + 1;
    });
    return Object.entries(counts)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([name, count]) => ({ name, count }));
  }, [reportData.buildingsList]);

  const buildingTypesInPhases = useMemo(() => {
    const grouped = {};
    reportData.buildingsList.forEach(b => {
      const phase = b.phaseName || "General";
      const type = b.typeName || "Unspecified";
      if (!grouped[phase]) grouped[phase] = {};
      grouped[phase][type] = (grouped[phase][type] || 0) + 1;
    });
    return Object.entries(grouped).sort((a, b) => a[0].localeCompare(b[0]));
  }, [reportData.buildingsList]);

  // Demographic metrics: Tenant vs Primary Occupant vs Dependent
  const demographicsData = useMemo(() => {
    const ten = filteredTenants.length;
    const po = reportData.primaryOccupantsCount;
    const dep = reportData.dependentsCount;
    const total = ten + po + dep;

    return {
      tenants: ten,
      primaryOccupants: po,
      dependents: dep,
      tenantsPct: total > 0 ? Math.round((ten / total) * 100) : 33,
      primaryPct: total > 0 ? Math.round((po / total) * 100) : 33,
      dependentsPct: total > 0 ? Math.round((dep / total) * 100) : 34,
    };
  }, [filteredTenants.length, reportData.primaryOccupantsCount, reportData.dependentsCount]);

  // Maintenance Column Chart Data
  const maintenanceChartData = useMemo(() => {
    const counts = { Electrical: 0, Plumbing: 0, Cleaning: 0, Structural: 0, General: 0 };
    reportData.requestsList.forEach(r => {
      const cat = r.category || "General";
      if (counts[cat] !== undefined) counts[cat]++;
      else counts.General++;
    });
    return Object.entries(counts).map(([name, count]) => ({ name, count }));
  }, [reportData.requestsList]);

  const requestsWeekly = useMemo(() => {
    const dayMs = 86400000;
    const tomorrow = new Date();
    tomorrow.setHours(0, 0, 0, 0);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const weeks = [];
    for (let i = 7; i >= 0; i--) {
      const end = new Date(tomorrow.getTime() - i * 7 * dayMs);
      const begin = new Date(end.getTime() - 7 * dayMs);
      weeks.push({ name: begin.toLocaleDateString("en-GB", { day: "numeric", month: "short" }), begin, end, open: 0, resolved: 0 });
    }
    reportData.requestsList.forEach((r) => {
      const d = r.createdDate ? new Date(r.createdDate) : null;
      if (!d || Number.isNaN(d.getTime())) return;
      const w = weeks.find((wk) => d >= wk.begin && d < wk.end);
      if (!w) return;
      if (isClosedRequest(r)) w.resolved += 1;
      else w.open += 1;
    });
    return weeks.map(({ name, open, resolved }) => ({ name, open, resolved }));
  }, [reportData.requestsList]);

  const requestsWeeklyTotal = requestsWeekly.reduce((a, w) => a + w.open + w.resolved, 0);
  const closedRequestsCount = reportData.requestsList.filter(isClosedRequest).length;
  const openRequestsCount = reportData.requestsList.length - closedRequestsCount;
  const resolutionRate = reportData.requestsList.length
    ? Math.round((closedRequestsCount / reportData.requestsList.length) * 100)
    : 0;

  const requestCategoryBreakdown = useMemo(() => {
    const counts = new Map();
    reportData.requestsList.forEach((r) => {
      const c = r.category || "General";
      counts.set(c, (counts.get(c) || 0) + 1);
    });
    const sorted = [...counts.entries()].sort((a, b) => b[1] - a[1]);
    const shown = sorted.length <= REQ_SERIES.length ? REQ_SERIES.length : REQ_SERIES.length - 1;
    const items = sorted.slice(0, shown).map(([name, count], i) => ({ name, count, color: REQ_SERIES[i] }));
    const rest = sorted.slice(shown).reduce((a, [, n]) => a + n, 0);
    if (rest > 0) items.push({ name: "Other", count: rest, color: REQ_OTHER });
    const total = reportData.requestsList.length;
    return {
      total,
      categories: sorted.length,
      items: items.map((it) => ({ ...it, pct: total ? Math.round((it.count / total) * 100) : 0 })),
    };
  }, [reportData.requestsList]);

  // Vehicle Column Chart Data & Summary Stats
  const topVehicleDetails = useMemo(() => {
    const counts = {};
    reportData.carsList.forEach(c => {
      const make = c.make || "Unknown";
      counts[make] = (counts[make] || 0) + 1;
    });
    let topMake = "N/A";
    let topCount = 0;
    Object.entries(counts).forEach(([mk, cnt]) => {
      if (cnt > topCount) { topMake = mk; topCount = cnt; }
    });
    return { topMake, topCount, counts };
  }, [reportData.carsList]);

  const vehicleChartData = useMemo(() => {
    return Object.entries(topVehicleDetails.counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([name, count]) => ({ name, count }));
  }, [topVehicleDetails.counts]);

  const handleExport = (type) => {
    toast.success(`Exporting report as ${type}...`);
    if (type === "PDF") {
      window.print();
    } else {
      try {
        const getOccupantsForBuilding = (bId) => {
          const occupants = reportData.tenantsList.filter(t => {
            const bList = getTenantBuildings(t);
            return bList.some(ab => String(ab.id || ab.buildingId) === String(bId));
          });
          return occupants.map(o => o.firstName && o.lastName ? `${o.firstName} ${o.lastName}`.trim() : (o.name || 'Unknown')).join(', ') || 'Vacant';
        };

        const overviewRows = [
          ["Total Buildings", reportData.totalBuildings],
          ["Occupied Properties", reportData.occupiedBuildingsCount],
          ["Vacant Properties", vacantBuildingsCount],
          ["Occupancy Rate", `${occupancyRate}%`],
          ["Yet to be Assigned Units", unassignedTenantsCount],
          ["Registered Tenants", reportData.totalTenants],
          ["Corporate Tenants", reportData.corporateTenants],
          ["Individual Tenants", reportData.totalTenants - reportData.corporateTenants],
          ["Primary Occupants", reportData.primaryOccupantsCount],
          ["Dependents", reportData.dependentsCount],
          ["Access Audits", reportData.totalAccessLogs],
          ["Active Visitors Onsite", reportData.activeVisitors],
          ["Total Rent Collected", fmtMoney(reportData.totalRentCollected)],
          ["Pending Rent", fmtMoney(reportData.pendingRent)],
          ["Maintenance Requests", reportData.totalMaintenanceRequests],
          ["Resolved Requests", reportData.resolvedRequestsCount],
          ["Pending Requests", reportData.pendingRequestsCount]
        ];

        const wb = XLSX.utils.book_new();

        const wsOverview = XLSX.utils.aoa_to_sheet([
          ["ZANGON ESTATE - SYSTEM ANALYTICS & EXECUTIVE REPORT"],
          [`Generated on: ${new Date().toLocaleString()} | Phase Count: ${reportData.totalPhases}`],
          [],
          ["Metric Column", "Value"],
          ...overviewRows
        ]);
        XLSX.utils.book_append_sheet(wb, wsOverview, "Overview Metrics");

        const buildingsData = reportData.buildingsList.map(b => ({
          "Building Number": b.number,
          "Phase": b.phaseName || '—',
          "Building Type": b.typeName || b.buildingTypeName || '—',
          "Current Occupant(s)": getOccupantsForBuilding(b.id)
        }));
        const wsBuildings = XLSX.utils.json_to_sheet(buildingsData);
        XLSX.utils.book_append_sheet(wb, wsBuildings, "Property Log");

        const tenantsData = reportData.tenantsList.map(t => {
          const name = t.name || `${t.firstName || ''} ${t.lastName || ''}`.trim() || 'Unknown';
          const props = t.assignedBuildings?.map(b => b.number).join(', ') || t.assignedBuilding?.number || 'Unassigned';
          const type = t.isCorporateTenant ? 'Corporate' : 'Individual';
          return {
            "Name": name,
            "Type": type,
            "Email Address": t.email || '—',
            "Phone Number": t.phoneNumber || '—',
            "Occupation / Sector": t.occupation || '—',
            "Properties": props,
            "Dependents": t.dependentCount || 0,
            "Presence Status": t.inEstate ?? true ? 'In Estate' : 'Out'
          };
        });
        const wsTenants = XLSX.utils.json_to_sheet(tenantsData);
        XLSX.utils.book_append_sheet(wb, wsTenants, "Tenants Log");

        const carsData = reportData.carsList.map(c => ({
          "License Plate": c.licensePlate,
          "Make": c.make || '—',
          "Model": c.model || '—',
          "Color": c.color || '—',
          "Year": c.year || '—',
          "Owner Name": c.ownerFullName || '—'
        }));
        const wsCars = XLSX.utils.json_to_sheet(carsData);
        XLSX.utils.book_append_sheet(wb, wsCars, "Vehicles Log");

        const requestsData = reportData.requestsList.map(r => ({
          "Title / Description": r.title || r.shortDescription || '—',
          "Category": r.category || 'General',
          "Priority": r.priorityLevel || 'Normal',
          "Status": r.status || 'Created',
          "Requester": r.requesterName || '—',
          "Created Date": r.createdDate ? new Date(r.createdDate).toLocaleDateString() : '—'
        }));
        const wsRequests = XLSX.utils.json_to_sheet(requestsData);
        XLSX.utils.book_append_sheet(wb, wsRequests, "Maintenance Requests");

        XLSX.writeFile(wb, `estate_analytics_report_${new Date().toISOString().split("T")[0]}.xlsx`);
        toast.success("Excel report exported successfully!");
      } catch (err) {
        toast.error("Failed to generate Excel download.");
        console.error(err);
      }
    }
  };

  const totalRentAmount = reportData.totalRentCollected + reportData.pendingRent;
  const collectionRate = totalRentAmount > 0 ? (reportData.totalRentCollected / totalRentAmount) * 100 : 0;
  const completedVisits = Math.max(0, reportData.totalAccessLogs - reportData.activeVisitors);

  return (
    <>
      <Dashboard6Styles />
      <PagePreloader visible={!pageReady} />

      <div className="d-flex flex-column flex-root app-root" id="kt_app_root">
        <div className="app-page flex-column flex-column-fluid" id="kt_app_page">
          
          {/* HEADER */}
          <div id="kt_app_header" className="app-header" data-kt-sticky="true" data-kt-sticky-activate="{default: false, lg: true}" data-kt-sticky-name="app-header-sticky" data-kt-sticky-offset="{default: false, lg: '300px'}">
            <div className="app-container container-fluid d-flex flex-stack align-items-center my-6" id="kt_app_header_container">
              <div className="d-flex align-items-center d-block d-lg-none ms-n3">
                <div className="btn btn-icon btn-active-color-primary w-35px h-35px me-2" id="kt_app_sidebar_mobile_toggle">
                  <i className="ki-outline ki-abstract-14 fs-2" />
                </div>
                <Link to="/dashboard"><img alt="Logo" src="/assets/media/zangon-fav.png" className="h-30px" /></Link>
              </div>

              <PageHeader breadcrumb="System Reports" title="Analytics & Reports">
                <button type="button" className="btn btn-light d-flex flex-center h-35px rounded-pill px-4 fs-8 fw-bold" onClick={() => handleExport("PDF")}>
                  <i className="ki-outline ki-file fs-6 me-sm-2" /><span className="d-none d-sm-inline">Print / PDF</span>
                </button>
                <button type="button" className="btn btn-success d-flex flex-center h-35px rounded-pill px-4 fs-8 fw-bold" onClick={() => handleExport("Excel")}>
                  <i className="ki-outline ki-file-down fs-6 me-sm-2 text-white" /><span className="d-none d-sm-inline">Export Excel</span>
                </button>
              </PageHeader>
            </div>
          </div>

          <div className="app-wrapper flex-column flex-row-fluid" id="kt_app_wrapper">
            <Sidebar collapsible />

            <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
              <div className="d-flex flex-column flex-column-fluid">
                <div id="kt_app_content" className="app-content flex-column-fluid">
                  <div id="kt_app_content_container" className="app-container container-fluid">

                     {isLoading ? (
                      <div className="text-center py-20">
                        <span className="spinner-border text-success fs-1"></span>
                        <div className="mt-4 fs-4 text-gray-600">Generating Reports &amp; Analytics...</div>
                      </div>
                    ) : (
                      <>

                        {/* 1. EXECUTIVE SUMMARY STATS BANNER */}
                        <KpiStrip
                          items={[
                            { icon: "ki-home", label: "Total Buildings", value: filteredBuildings.length, sub: "Estate properties" },
                            { icon: "ki-user-square", label: "Yet to be Assigned", value: unassignedTenantsCount, sub: "Awaiting allocation" },
                            { icon: "ki-user", label: "Registered Tenants", value: filteredTenants.length, sub: "Resident tenants" },
                            { icon: "ki-address-book", label: "Access Audits", value: reportData.totalAccessLogs, sub: "Entries & exits" },
                          ]}
                        />

                        {/* 2. OCCUPANCY & FINANCIAL INTELLIGENCE */}
                        <div className="d6-card p-6 mb-5">
                          <CardHead title="Executive Overview" subtitle="Property occupancy, financial collection, and access analytics" className="mb-5">
                            <select
                              className="d6m-input"
                              style={{ width: 170, minHeight: 40, height: 40, borderRadius: 999 }}
                              value={phaseFilter}
                              onChange={(e) => setPhaseFilter(e.target.value)}
                            >
                              <option value="all">All Estate Phases</option>
                              {uniquePhaseList.map(p => (
                                <option key={p} value={p}>{p}</option>
                              ))}
                            </select>
                            <select
                              className="d6m-input"
                              style={{ width: 150, minHeight: 40, height: 40, borderRadius: 999 }}
                              value={timeframe}
                              onChange={(e) => setTimeframe(e.target.value)}
                            >
                              <option value="all">All Time</option>
                              <option value="30days">Last 30 Days</option>
                              <option value="quarter">This Quarter</option>
                              <option value="year">This Year</option>
                            </select>
                            {(phaseFilter !== "all" || timeframe !== "all") && (
                              <button
                                type="button"
                                className="btn btn-light d6-mbtn"
                                onClick={() => { setPhaseFilter("all"); setTimeframe("all"); }}
                              >
                                Reset
                              </button>
                            )}
                          </CardHead>

                          <div>
                            <div className="row g-5">

                              {/* Occupancy Rate Gauge Card */}
                              <div className="col-12 col-md-4">
                                <div className="p-6 bg-light-primary bg-opacity-30 border border-primary h-100 d-flex flex-column justify-content-between" style={{ borderRadius: "14px" }}>
                                  <div className="d-flex align-items-center justify-content-between mb-3">
                                    <h5 className="fw-bold text-gray-800 fs-6 m-0">Property Occupancy</h5>
                                    <span className="badge badge-light-primary fs-7 fw-bold">{occupancyRate}%</span>
                                  </div>
                                  <div className="my-4 text-center">
                                    <span className="fs-3hx fw-bolder text-primary d-block">{occupancyRate}%</span>
                                    <span className="text-gray-600 fs-8 fw-semibold">Units Currently Occupied</span>
                                  </div>
                                  <div className="w-100 bg-gray-200 rounded-pill h-10px overflow-hidden mb-4">
                                    <div className="bg-primary rounded-pill h-100 transition-3ms" style={{ width: `${occupancyRate}%` }}></div>
                                  </div>
                                  <div className="d-flex justify-content-between text-gray-700 fs-8 border-top pt-3">
                                    <span>Occupied: <strong className="text-success">{filteredBuildings.length - vacantBuildingsCount}</strong></span>
                                    <span>Vacant: <strong className="text-danger">{vacantBuildingsCount}</strong></span>
                                    <span>Total: <strong>{filteredBuildings.length}</strong></span>
                                  </div>
                                </div>
                              </div>

                              {/* Financial Analytics Card */}
                              <div className="col-12 col-md-4">
                                <div className="p-6 bg-light-success bg-opacity-30 border border-success h-100 d-flex flex-column justify-content-between" style={{ borderRadius: "14px" }}>
                                  <div className="d-flex align-items-center justify-content-between mb-3">
                                    <h5 className="fw-bold text-gray-800 fs-6 m-0">Revenue &amp; Dues</h5>
                                    <span className="badge badge-light-success fs-8 fw-bold">{collectionRate.toFixed(1)}% Paid</span>
                                  </div>
                                  <div className="row g-3 my-2 text-center">
                                    <div className="col-6">
                                      <span className="fs-7 text-gray-600 d-block">Collected</span>
                                      <span className="fs-4 fw-bold text-success">{fmtMoney(reportData.totalRentCollected)}</span>
                                    </div>
                                    <div className="col-6 border-start">
                                      <span className="fs-7 text-gray-600 d-block">Outstanding</span>
                                      <span className="fs-4 fw-bold text-danger">{fmtMoney(reportData.pendingRent)}</span>
                                    </div>
                                  </div>
                                  <div className="w-100 bg-gray-200 rounded-pill h-10px overflow-hidden mb-4">
                                    <div className="bg-success rounded-pill h-100 transition-3ms" style={{ width: `${collectionRate}%` }}></div>
                                  </div>
                                  <div className="d-flex justify-content-between align-items-center text-gray-700 fs-8 border-top pt-3">
                                    <span>Owing Accounts:</span>
                                    <span className="badge badge-light-danger fw-bold">{reportData.outstandingPaymentsCount} Invoices</span>
                                  </div>
                                </div>
                              </div>

                              {/* Security & Access Audit Card */}
                              <div className="col-12 col-md-4">
                                <div className="p-6 bg-light-warning bg-opacity-30 border border-warning h-100 d-flex flex-column justify-content-between" style={{ borderRadius: "14px" }}>
                                  <div className="d-flex align-items-center justify-content-between mb-3">
                                    <h5 className="fw-bold text-gray-800 fs-6 m-0">Access &amp; Gate Control</h5>
                                    <span className="badge badge-light-warning fs-8 fw-bold">{reportData.activeVisitors} Onsite</span>
                                  </div>
                                  <div className="row g-3 my-2 text-center">
                                    <div className="col-6">
                                      <span className="fs-7 text-gray-600 d-block">Total Logs</span>
                                      <span className="fs-4 fw-bold text-gray-900">{reportData.totalAccessLogs}</span>
                                    </div>
                                    <div className="col-6 border-start">
                                      <span className="fs-7 text-gray-600 d-block">Completed</span>
                                      <span className="fs-4 fw-bold text-success">{completedVisits}</span>
                                    </div>
                                  </div>
                                  <div className="border-top pt-3 d-flex justify-content-between align-items-center fs-8 text-gray-700">
                                    <span>Gate Operations:</span>
                                    <span className="badge badge-light-primary fw-bold">Live Monitoring</span>
                                  </div>
                                </div>
                              </div>

                            </div>
                          </div>
                        </div>

                        {/* RENT COLLECTION: collected vs outstanding */}
                        <style>{`
                          .rp-seg-bar { display: flex; gap: 3px; height: 44px; }
                          .rp-seg-bar > div { height: 100%; border-radius: 8px; min-width: 6px; transition: width 0.9s cubic-bezier(0.16, 1, 0.3, 1); }
                          .rp-inset { background: #f8faf9; border: 1px solid #f0f2f4; border-radius: 12px; }
                          [data-bs-theme="dark"] .rp-inset { background: rgba(255,255,255,0.02); border-color: #2b2b40; }
                          .rp-inset-row + .rp-inset-row { border-top: 1px solid #eceef1; }
                          [data-bs-theme="dark"] .rp-inset-row + .rp-inset-row { border-color: #2b2b40; }
                        `}</style>
                        {(() => {
                          const collectedPct = Math.round(collectionRate);
                          const pendingPct = 100 - collectedPct;
                          const RENT_TRACK = "#DCF3E4";
                          return (
                            <div className="d6-card mb-5">
                              <div className="row g-0">
                                <div className="col-12 col-lg-7 p-6 p-lg-8 d-flex flex-column gap-5">
                                  <div className="d-flex align-items-start justify-content-between flex-wrap gap-3">
                                    <div>
                                      <h3 className="fw-bold text-gray-900 fs-4 mb-1">Rent Collection</h3>
                                      <span className="text-gray-500 fs-7">Collected vs outstanding on active leases</span>
                                    </div>
                                    <Link to="/rent-management" className="btn btn-light-success d-flex flex-center h-35px rounded-pill px-4 fs-8 fw-bold">
                                      <i className="ki-outline ki-wallet fs-6 me-2"></i>Rent Management
                                    </Link>
                                  </div>

                                  <div>
                                    <div className="d-flex align-items-baseline gap-2 flex-wrap">
                                      <span className="text-gray-900" style={{ fontSize: 30, fontWeight: 500, letterSpacing: "-0.02em", lineHeight: 1 }}>
                                        {fmtMoney(totalRentAmount)}
                                      </span>
                                      <span className="text-gray-400 fs-8">expected this cycle</span>
                                    </div>
                                    <div className="text-gray-400 fs-8 mt-2">
                                      {reportData.outstandingPaymentsCount} tenants still owing ·{" "}
                                      <span className="text-success fw-semibold">{collectedPct}% collected</span>
                                    </div>
                                  </div>

                                  <div>
                                    <div className="d-flex fs-8 fw-semibold text-gray-700 mb-2">
                                      <span className="border-start border-gray-300 ps-2 text-truncate" style={{ width: `${collectedPct}%`, minWidth: 44 }}>{collectedPct}%</span>
                                      <span className="border-start border-gray-300 ps-2 text-truncate" style={{ width: `${pendingPct}%`, minWidth: 44 }}>{pendingPct}%</span>
                                    </div>
                                    <div className="rp-seg-bar">
                                      <div style={{ width: `${collectedPct}%`, background: REQ_SERIES[0] }} title={`Collected: ${fmtMoney(reportData.totalRentCollected)}`} />
                                      <div style={{ width: `${pendingPct}%`, background: RENT_TRACK }} title={`Pending: ${fmtMoney(reportData.pendingRent)}`} />
                                    </div>
                                    <div className="d-flex flex-wrap gap-4 mt-3 fs-8 text-gray-500">
                                      <span className="d-flex align-items-center gap-2"><span className="rp-dot" style={{ background: REQ_SERIES[0] }} />Collected</span>
                                      <span className="d-flex align-items-center gap-2"><span className="rp-dot" style={{ background: RENT_TRACK, boxShadow: "inset 0 0 0 1px #b7e4c7" }} />Pending</span>
                                    </div>
                                  </div>
                                </div>

                                <div className="col-12 col-lg-5 p-6 p-lg-8 d-flex align-items-center">
                                  <div className="rp-inset px-4 w-100">
                                    {[
                                      { label: "Rent collected", value: fmtMoney(reportData.totalRentCollected), pct: `${collectedPct}%` },
                                      { label: "Pending rent", value: fmtMoney(reportData.pendingRent), pct: `${pendingPct}%` },
                                      { label: "Tenants owing", value: reportData.outstandingPaymentsCount, pct: "" },
                                    ].map((row) => (
                                      <div key={row.label} className="rp-inset-row d-flex align-items-center justify-content-between py-4">
                                        <span className="fs-7 text-gray-700">{row.label}</span>
                                        <span className="d-flex align-items-center gap-3">
                                          <span className="fs-7 fw-semibold text-gray-900">{row.value}</span>
                                          <span className="fs-8 text-gray-400 text-end" style={{ width: 36 }}>{row.pct}</span>
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })()}

                        {/* 3. CHARTS AND DEMOGRAPHICS */}
                        <div className="row g-6 mb-6">
                          
                          {/* Buildings by Phase Chart */}
                          <div className="col-12 col-xl-6">
                            <div className="d6-card p-6 h-100">
                              <CardHead title="Buildings by Phase" subtitle="Distribution of properties across estate phases" />
                              <div>
                                {buildingsPerPhase.length === 0 ? (
                                  <div className="d6m-empty">No building phase details found.</div>
                                ) : (
                                  <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={buildingsPerPhase} margin={{ top: 20, right: 30, left: -20, bottom: 5 }}>
                                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E4E6EF" />
                                      <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fill: '#A1A5B7', fontSize: 12}} />
                                      <YAxis axisLine={false} tickLine={false} tick={{fill: '#A1A5B7', fontSize: 12}} allowDecimals={false} />
                                      <Tooltip cursor={{fill: '#F5F8FA'}} contentStyle={{ borderRadius: '8px', border: 'none' }} />
                                      <Bar dataKey="count" fill="#12B76A" radius={[4, 4, 0, 0]} barSize={40} />
                                    </BarChart>
                                  </ResponsiveContainer>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Demographics: Tenant vs Primary Occupant vs Dependent */}
                          <div className="col-12 col-xl-6">
                            <div className="d6-card p-6 h-100">
                              <CardHead title="Population Demographics" subtitle="Tenants, Primary Occupants, and Dependents breakdown" />
                              <div className="d-flex flex-column justify-content-center">
                                
                                {/* Tenants */}
                                <div className="d-flex flex-stack mb-5">
                                  <div className="d-flex align-items-center me-3">
                                    <div className="symbol symbol-40px me-3">
                                      <div className="symbol-label bg-light-success">
                                        <i className="ki-outline ki-user fs-2 text-success"></i>
                                      </div>
                                    </div>
                                    <div className="d-flex flex-column">
                                      <span className="text-gray-900 fw-bold fs-6">Tenants</span>
                                      <span className="text-muted fw-semibold fs-7">Registered property leaseholders</span>
                                    </div>
                                  </div>
                                  <div className="d-flex align-items-center">
                                    <span className="text-gray-900 fw-bold fs-4 me-2">{demographicsData.tenants}</span>
                                    <span className="badge badge-light-success">{demographicsData.tenantsPct}%</span>
                                  </div>
                                </div>

                                {/* Primary Occupants */}
                                <div className="d-flex flex-stack mb-5">
                                  <div className="d-flex align-items-center me-3">
                                    <div className="symbol symbol-40px me-3">
                                      <div className="symbol-label bg-light-primary">
                                        <i className="ki-outline ki-profile-user fs-2 text-primary"></i>
                                      </div>
                                    </div>
                                    <div className="d-flex flex-column">
                                      <span className="text-gray-900 fw-bold fs-6">Primary Occupants</span>
                                      <span className="text-muted fw-semibold fs-7">Registered heads of house</span>
                                    </div>
                                  </div>
                                  <div className="d-flex align-items-center">
                                    <span className="text-gray-900 fw-bold fs-4 me-2">{demographicsData.primaryOccupants}</span>
                                    <span className="badge badge-light-primary">{demographicsData.primaryPct}%</span>
                                  </div>
                                </div>

                                {/* Dependents */}
                                <div className="d-flex flex-stack mb-5">
                                  <div className="d-flex align-items-center me-3">
                                    <div className="symbol symbol-40px me-3">
                                      <div className="symbol-label bg-light-info">
                                        <i className="ki-outline ki-people fs-2 text-info"></i>
                                      </div>
                                    </div>
                                    <div className="d-flex flex-column">
                                      <span className="text-gray-900 fw-bold fs-6">Dependents</span>
                                      <span className="text-muted fw-semibold fs-7">Family members &amp; registered staff</span>
                                    </div>
                                  </div>
                                  <div className="d-flex align-items-center">
                                    <span className="text-gray-900 fw-bold fs-4 me-2">{demographicsData.dependents}</span>
                                    <span className="badge badge-light-info">{demographicsData.dependentsPct}%</span>
                                  </div>
                                </div>

                                {/* Multi-color Ratio Bar */}
                                <div className="d-flex flex-column mt-auto pt-3">
                                  <div className="d-flex justify-content-between text-gray-600 fs-8 fw-semibold mb-2">
                                    <span>Ratio Distribution (Tenants / Occupants / Dependents)</span>
                                    <span>100%</span>
                                  </div>
                                  <div className="h-40px w-100 bg-gray-200 rounded d-flex overflow-hidden p-1 gap-1">
                                    <div 
                                      className="bg-success rounded h-100 transition-3ms" 
                                      style={{ width: `${demographicsData.tenantsPct}%` }}
                                      title={`Tenants: ${demographicsData.tenantsPct}%`}
                                    ></div>
                                    <div 
                                      className="bg-primary rounded h-100 transition-3ms" 
                                      style={{ width: `${demographicsData.primaryPct}%` }}
                                      title={`Primary Occupants: ${demographicsData.primaryPct}%`}
                                    ></div>
                                    <div 
                                      className="bg-info rounded h-100 transition-3ms" 
                                      style={{ width: `${demographicsData.dependentsPct}%` }}
                                      title={`Dependents: ${demographicsData.dependentsPct}%`}
                                    ></div>
                                  </div>
                                </div>

                              </div>
                            </div>
                          </div>

                        </div>

                        {/* 4. MAPPED BUILDING TYPES IN PHASES */}
                        <div className="d6-card p-6 mb-5">
                          <CardHead title="Building Types Mapped in Phases" subtitle="Detailed classification of structures in each estate phase" />
                          <div>
                            {buildingTypesInPhases.length === 0 ? (
                              <div className="d6m-empty">No building types or phases found.</div>
                            ) : (
                              <div className="row g-5">
                                {buildingTypesInPhases.map(([phase, typesObj]) => (
                                  <div key={phase} className="col-12 col-md-6 col-xl-4">
                                    <div className="d6-card h-100 overflow-hidden">
                                      <div className="px-4 py-3 d-flex align-items-center justify-content-between border-bottom border-gray-200 bg-light">
                                        <span className="fw-bold text-gray-900 fs-7">{phase}</span>
                                        <span className="badge bg-light-success text-success fs-9 fw-semibold rounded-pill px-2.5 py-1">
                                          {Object.values(typesObj).reduce((a, b) => a + b, 0)} Units
                                        </span>
                                      </div>
                                      <div className="p-4 d-flex flex-column gap-3">
                                        {Object.entries(typesObj).map(([type, count]) => (
                                          <div key={type} className="d-flex align-items-center justify-content-between border-bottom pb-2 pt-2 fs-7">
                                            <div className="d-flex align-items-center gap-3">
                                              <div className="rounded bg-white overflow-hidden h-40px w-40px border flex-shrink-0">
                                                <img src={getHouseImage(type)} alt={type} className="w-100 h-100 object-fit-cover" />
                                              </div>
                                              <span className="text-gray-600 fw-semibold">{type}</span>
                                            </div>
                                            <span className="text-gray-900 fw-bold">{count}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* MAINTENANCE TRENDS: opened vs resolved + by category */}
                        <style>{`
                          .rp-split { display: grid; grid-template-columns: minmax(0, 1fr); }
                          @media (min-width: 992px) { .rp-split { grid-template-columns: minmax(0, 2fr) minmax(0, 1fr); } }
                          .rp-aside { background: #f8faf9; border-top: 1px solid #eceef1; }
                          @media (min-width: 992px) { .rp-aside { border-top: 0; border-left: 1px solid #eceef1; } }
                          [data-bs-theme="dark"] .rp-aside { background: rgba(255,255,255,0.02); border-color: #2b2b40; }
                          .rp-dot { width: 8px; height: 8px; border-radius: 999px; display: inline-block; flex-shrink: 0; }
                          .rp-thin-bar { display: flex; gap: 2px; height: 8px; }
                          .rp-thin-bar > div { height: 100%; border-radius: 3px; min-width: 3px; }
                          .rp-row + .rp-row { border-top: 1px solid #eceef1; }
                          [data-bs-theme="dark"] .rp-row + .rp-row { border-color: #2b2b40; }
                          .rp-tooltip { background: #ffffff; border: 1px solid #e4e7eb; border-radius: 12px; padding: 8px 12px; min-width: 150px; box-shadow: 0 10px 24px -8px rgba(15, 23, 42, 0.18); }
                        `}</style>
                        <div className="d6-card overflow-hidden mb-5">
                          <div className="rp-split">
                            <div className="p-6 p-lg-8 d-flex flex-column" style={{ minHeight: 380 }}>
                              <div className="d-flex align-items-start justify-content-between flex-wrap gap-3 mb-3">
                                <div>
                                  <h3 className="fw-bold text-gray-900 fs-4 mb-1">Maintenance Requests</h3>
                                  <span className="text-gray-500 fs-7">Opened vs resolved per week, last 8 weeks</span>
                                </div>
                                <Link to="/tenant-requests" className="btn btn-light-success d-flex flex-center h-35px rounded-pill px-4 fs-8 fw-bold">
                                  <i className="ki-outline ki-eye fs-6 me-2"></i>View All
                                </Link>
                              </div>

                              <div className="d-flex flex-wrap align-items-end gap-6 mb-3">
                                <div>
                                  <div className="d-flex align-items-baseline gap-2">
                                    <span className="text-gray-900 fw-bolder" style={{ fontSize: "1.9rem", letterSpacing: "-0.03em", lineHeight: 1 }}>
                                      {closedRequestsCount}
                                    </span>
                                    <span className="fs-8 text-gray-400">resolved · {openRequestsCount} still open</span>
                                  </div>
                                  <div className="d-flex align-items-center gap-4 mt-2 fs-8 text-gray-500">
                                    <span className="d-flex align-items-center gap-2"><span className="rp-dot" style={{ background: REQ_SERIES[0] }} />Resolved</span>
                                    <span className="d-flex align-items-center gap-2"><span className="rp-dot" style={{ background: REQ_SERIES[1] }} />Open</span>
                                  </div>
                                </div>
                                <div className="ms-auto text-end">
                                  <div className="fs-8 fw-semibold text-gray-500 mb-1">Resolution rate</div>
                                  <div className="fs-4 fw-bold text-gray-900">{resolutionRate}%</div>
                                </div>
                              </div>

                              <div className="position-relative mt-auto" style={{ width: "100%", height: 250 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                  <BarChart data={requestsWeekly} margin={{ top: 8, right: 4, left: -24, bottom: 0 }}>
                                    <CartesianGrid vertical={false} stroke="#f1f3f5" />
                                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: "#94a3b8", fontSize: 11, fontWeight: 500 }} dy={6} />
                                    <YAxis axisLine={false} tickLine={false} allowDecimals={false} tick={{ fill: "#94a3b8", fontSize: 11 }} />
                                    <Tooltip content={<RequestsTooltip />} cursor={{ fill: "rgba(11, 148, 70, 0.05)" }} />
                                    <Bar name="Resolved" dataKey="resolved" stackId="req" fill={REQ_SERIES[0]} maxBarSize={24} stroke="#ffffff" strokeWidth={2} />
                                    <Bar name="Open" dataKey="open" stackId="req" fill={REQ_SERIES[1]} maxBarSize={24} radius={[4, 4, 0, 0]} stroke="#ffffff" strokeWidth={2} />
                                  </BarChart>
                                </ResponsiveContainer>
                                {requestsWeeklyTotal === 0 && (
                                  <div className="position-absolute top-50 start-50 translate-middle text-center">
                                    <div className="fs-7 fw-bold text-gray-700">No requests in the last 8 weeks</div>
                                    <div className="fs-8 text-gray-400">New requests from residents will chart here.</div>
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="rp-aside p-6 p-lg-8 d-flex flex-column gap-4">
                              <div className="d-flex align-items-start justify-content-between gap-3">
                                <h3 className="fw-bold text-gray-900 fs-4 mb-0">Requests by Category</h3>
                                <span className="fs-8 text-gray-400 text-nowrap">
                                  {requestCategoryBreakdown.categories} {requestCategoryBreakdown.categories === 1 ? "category" : "categories"}
                                </span>
                              </div>

                              <div>
                                <div className="d-flex align-items-baseline gap-2">
                                  <span className="text-gray-900 fw-bolder" style={{ fontSize: "1.9rem", letterSpacing: "-0.03em", lineHeight: 1 }}>
                                    {requestCategoryBreakdown.total}
                                  </span>
                                  <span className="fs-8 text-gray-400">requests logged</span>
                                </div>
                                {requestCategoryBreakdown.total > 0 && (
                                  <div className="rp-thin-bar mt-3">
                                    {requestCategoryBreakdown.items.map((c) => (
                                      <div key={c.name} style={{ width: `${c.pct}%`, background: c.color }} title={`${c.name}: ${c.count}`} />
                                    ))}
                                  </div>
                                )}
                              </div>

                              <div className="d-flex flex-column flex-grow-1 overflow-y-auto pe-1" style={{ maxHeight: 220 }}>
                                {requestCategoryBreakdown.items.length === 0 ? (
                                  <div className="fs-8 text-gray-400 py-4">No requests logged yet.</div>
                                ) : (
                                  requestCategoryBreakdown.items.map((c) => (
                                    <div key={c.name} className="rp-row d-flex align-items-center justify-content-between py-2">
                                      <span className="d-flex align-items-center gap-2 min-w-0 fs-7 text-gray-700">
                                        <span className="rp-dot" style={{ background: c.color }} />
                                        <span className="text-truncate">{c.name}</span>
                                      </span>
                                      <span className="d-flex align-items-center gap-3 flex-shrink-0">
                                        <span className="fs-7 fw-bold text-gray-900">{c.count}</span>
                                        <span className="fs-8 fw-semibold text-gray-400 text-end" style={{ width: 36 }}>{c.pct}%</span>
                                      </span>
                                    </div>
                                  ))
                                )}
                              </div>

                              <Link to="/tenant-requests" className="btn w-100 rounded-3 fs-7 fw-semibold py-3 text-white" style={{ backgroundColor: "#065d38" }}>
                                Open Request Queue
                              </Link>
                            </div>
                          </div>
                        </div>

                        {/* 5. OPERATIONS & MAINTENANCE + VEHICLE COLUMN CHARTS */}
                        <div className="row g-6 mb-6">
                          
                          {/* Maintenance Category Column Chart */}
                          <div className="col-12 col-xl-6">
                            <div className="d6-card p-6 h-100">
                              <CardHead title="Maintenance & Requests" subtitle="Category distribution and resolution progress" />
                              <div>
                               

                                {/* Column Bar Chart */}
                                {reportData.totalMaintenanceRequests === 0 ? (
                                  <div className="d6m-empty">No maintenance requests logged.</div>
                                ) : (
                                  <ResponsiveContainer width="100%" height={260}>
                                    <BarChart data={maintenanceChartData} margin={{ top: 10, right: 20, left: -20, bottom: 5 }}>
                                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E4E6EF" />
                                      <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: "#A1A5B7", fontSize: 12 }} />
                                      <YAxis axisLine={false} tickLine={false} tick={{ fill: "#A1A5B7", fontSize: 12 }} allowDecimals={false} />
                                      <Tooltip cursor={{ fill: "#F5F8FA" }} contentStyle={{ borderRadius: "8px", border: "none" }} />
                                      <Bar dataKey="count" fill="#F59E0B" radius={[4, 4, 0, 0]} barSize={38} />
                                    </BarChart>
                                  </ResponsiveContainer>
                                )}

                                {/* 3-Pill Bottom Stat Cards */}
                                <div className="row g-3 mt-3 pt-2 border-top border-gray-100">
                                  <div className="col-4">
                                    <div className="bg-light bg-opacity-60 rounded-3 p-3 text-center border border-gray-200">
                                      <span className="fs-8 fw-semibold text-gray-500 d-block mb-1">Total Raised</span>
                                      <span className="fs-5 fw-bold text-gray-900">{reportData.totalMaintenanceRequests}</span>
                                    </div>
                                  </div>
                                  <div className="col-4">
                                    <div className="bg-light-success bg-opacity-25 rounded-3 p-3 text-center border border-success border-opacity-20">
                                      <span className="fs-8 fw-semibold text-success d-block mb-1">Completed</span>
                                      <span className="fs-5 fw-bold text-success">{reportData.resolvedRequestsCount}</span>
                                    </div>
                                  </div>
                                  <div className="col-4">
                                    <div className="bg-light-warning bg-opacity-25 rounded-3 p-3 text-center border border-warning border-opacity-20">
                                      <span className="fs-8 fw-semibold text-warning d-block mb-1">Pending</span>
                                      <span className="fs-5 fw-bold text-warning">{reportData.pendingRequestsCount}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Vehicles by Make Column Chart */}
                          <div className="col-12 col-xl-6">
                            <div className="d6-card p-6 h-100">
                              <CardHead title="Estate Registered Vehicles" subtitle="Vehicle logs and manufacturer breakdown" />
                              <div>
                               

                                {/* Column Bar Chart */}
                                {vehicleChartData.length === 0 ? (
                                  <div className="d6m-empty">No registered vehicles found.</div>
                                ) : (
                                  <ResponsiveContainer width="100%" height={260}>
                                    <BarChart data={vehicleChartData} margin={{ top: 10, right: 20, left: -20, bottom: 5 }}>
                                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E4E6EF" />
                                      <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: "#A1A5B7", fontSize: 12 }} />
                                      <YAxis axisLine={false} tickLine={false} tick={{ fill: "#A1A5B7", fontSize: 12 }} allowDecimals={false} />
                                      <Tooltip cursor={{ fill: "#F5F8FA" }} contentStyle={{ borderRadius: "8px", border: "none" }} />
                                      <Bar dataKey="count" fill="#3B82F6" radius={[4, 4, 0, 0]} barSize={38} />
                                    </BarChart>
                                  </ResponsiveContainer>
                                )}

                                {/* 3-Pill Bottom Stat Cards */}
                                <div className="row g-3 mt-3 pt-2 border-top border-gray-100">
                                  <div className="col-4">
                                    <div className="bg-light bg-opacity-60 rounded-3 p-3 text-center border border-gray-200">
                                      <span className="fs-8 fw-semibold text-gray-500 d-block mb-1">Total Fleet</span>
                                      <span className="fs-5 fw-bold text-gray-900">{reportData.carsList.length}</span>
                                    </div>
                                  </div>
                                  <div className="col-4">
                                    <div className="bg-light-primary bg-opacity-25 rounded-3 p-3 text-center border border-primary border-opacity-20">
                                      <span className="fs-8 fw-semibold text-primary d-block mb-1">Top Brand</span>
                                      <span className="fs-5 fw-bold text-primary text-truncate d-block">
                                        {topVehicleDetails.topMake || "—"}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="col-4">
                                    <div className="bg-light-info bg-opacity-25 rounded-3 p-3 text-center border border-info border-opacity-20">
                                      <span className="fs-8 fw-semibold text-info d-block mb-1">Brand Count</span>
                                      <span className="fs-5 fw-bold text-info">
                                        {topVehicleDetails.topCount || 0}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                        </div>
                      </>
                    )}

                    <div className="my-10"></div>
                  </div>
                </div>
              </div>

              <div id="kt_app_footer" className="app-footer">
                <div className="app-container container-fluid d-flex justify-content-center align-items-center py-3">
                  <div className="text-gray-900 text-center">
                    <span className="text-muted fw-semibold me-1">2026&copy;</span>
                    <a href="https://pixels.com.ng/" target="_blank" rel="noreferrer" className="text-gray-800 text-hover-primary">Powered by Pixels Solutions Limited</a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div id="kt_scrolltop" className="scrolltop bg-success" data-kt-scrolltop="true">
            <i className="ki-outline ki-arrow-up"></i>
          </div>
        </div>
      </div>
    </>
  );
}